<template>
  <div class="announcements-page">
    <!-- 横幅部分 -->
    <section class="hero-banner bg-orange-500 py-16 lg:py-20">
      <div class="max-w-7xl mx-auto px-4 lg:px-8">
        <div class="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12">
          <!-- 左侧内容 -->
          <div class="flex-1 text-center lg:text-left">
            <h1 class="text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-6 leading-tight">
              {{ $t('announcements.title') }}
            </h1>
            <p class="text-xl text-white mb-8 opacity-90">
              {{ $t('announcements.subtitle') }}
            </p>
          </div>

          <!-- 右侧图标 -->
          <div class="flex-shrink-0 w-64 h-64 lg:w-80 lg:h-80">
            <img :src="banner.image_url || '/assets/23@1x.png'" :alt="$t('announcements.title')" class="w-full h-full object-contain">
          </div>
        </div>
      </div>
    </section>

    <!-- 公告列表部分 -->
    <section class="announcements-content py-16 bg-white">
      <div class="max-w-7xl mx-auto px-4 lg:px-8">
        <!-- 分类筛选 -->
        <div class="mb-8">
          <div class="flex flex-wrap gap-3">
            <button
              @click="filterCategory('all')"
              :class="['px-6 py-2 rounded-full font-medium transition-colors', selectedCategory === 'all' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200']"
            >
              {{ $t('announcements.all') }}
            </button>
            <button
              v-for="category in categories"
              :key="category.key"
              @click="filterCategory(category.key)"
              :class="['px-6 py-2 rounded-full font-medium transition-colors', selectedCategory === category.key ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200']"
            >
              {{ getCategoryLabel(category.key) }}
            </button>
          </div>
        </div>

        <!-- 公告卡片列表 -->
        <div class="grid grid-cols-1 gap-6">
          <div
            v-for="announcement in filteredAnnouncements"
            :key="announcement.id"
            class="announcement-card border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-all cursor-pointer"
            @click="viewAnnouncement(announcement)"
          >
            <div class="flex flex-col lg:flex-row lg:items-start gap-4">
              <!-- 发布日期 -->
              <div class="flex-shrink-0">
                <div class="bg-orange-50 rounded-lg p-4 text-center min-w-[80px]">
                  <div class="text-2xl font-bold text-orange-600">{{ getDay(announcement.publish_date) }}</div>
                  <div class="text-sm text-gray-600">{{ getMonthYear(announcement.publish_date) }}</div>
                </div>
              </div>

              <!-- 公告内容 -->
              <div class="flex-1">
                <div class="flex items-start justify-between mb-3">
                  <div>
                    <span
                      v-if="announcement.category"
                      :class="['inline-block px-3 py-1 text-xs font-medium rounded-full mb-2', getCategoryClass(announcement.category)]"
                    >
                      {{ getCategoryLabel(announcement.category) }}
                    </span>
                    <h3 class="text-xl font-bold text-gray-900 mb-2">
                      {{ announcement.title }}
                    </h3>
                  </div>
                  <span v-if="announcement.is_top" class="flex-shrink-0 ml-2 px-2 py-1 bg-red-500 text-white text-xs rounded">
                    {{ $t('announcements.pinned') }}
                  </span>
                </div>

                <div class="text-gray-600 text-sm mb-4 line-clamp-2" v-html="announcement.summary"></div>

                <div class="flex items-center justify-between">
                  <span class="text-xs text-gray-500">
                    {{ $t('announcements.views') }}: {{ announcement.views || 0 }}
                  </span>
                  <span class="text-orange-600 hover:text-orange-700 text-sm font-medium">
                    {{ $t('announcements.readMore') }} →
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 分页 -->
        <div v-if="totalPages > 1" class="mt-8">
          <div class="flex justify-center items-center gap-2">
            <button
              @click="goToPage(currentPage - 1)"
              :disabled="currentPage === 1"
              :class="['px-4 py-2 rounded-lg transition-colors', currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-100 text-gray-700 hover:bg-gray-200']"
            >
              {{ $t('announcements.previous') }}
            </button>

            <div v-for="page in displayedPages" :key="page">
              <button
                v-if="page === '...'"
                class="px-4 py-2 text-gray-400"
                disabled
              >
                ...
              </button>
              <button
                v-else
                @click="goToPage(page)"
                :class="['px-4 py-2 rounded-lg transition-colors', page === currentPage ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200']"
              >
                {{ page }}
              </button>
            </div>

            <button
              @click="goToPage(currentPage + 1)"
              :disabled="currentPage === totalPages"
              :class="['px-4 py-2 rounded-lg transition-colors', currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-100 text-gray-700 hover:bg-gray-200']"
            >
              {{ $t('announcements.next') }}
            </button>
          </div>
        </div>

        <!-- 空状态 -->
        <div v-if="filteredAnnouncements.length === 0" class="text-center py-16">
          <svg class="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p class="text-gray-500 text-lg">{{ $t('announcements.noAnnouncements') }}</p>
        </div>
      </div>
    </section>

    <!-- 公告详情弹窗 -->
    <div v-if="selectedAnnouncement" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" @click.self="closeDetail">
      <div class="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div class="p-6 lg:p-8">
          <!-- 头部 -->
          <div class="flex items-start justify-between mb-6">
            <div class="flex-1">
              <span
                v-if="selectedAnnouncement.category"
                :class="['inline-block px-3 py-1 text-xs font-medium rounded-full mb-3', getCategoryClass(selectedAnnouncement.category)]"
              >
                {{ getCategoryLabel(selectedAnnouncement.category) }}
              </span>
              <h2 class="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
                {{ selectedAnnouncement.title }}
              </h2>
              <div class="flex items-center gap-4 text-sm text-gray-500">
                <span>{{ formatDate(selectedAnnouncement.publish_date) }}</span>
                <span>{{ $t('announcements.views') }}: {{ selectedAnnouncement.views || 0 }}</span>
              </div>
            </div>
            <button @click="closeDetail" class="flex-shrink-0 ml-4 text-gray-400 hover:text-gray-600">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- 内容 -->
          <div class="prose max-w-none">
            <div v-html="selectedAnnouncement.content" class="text-gray-700"></div>
          </div>

          <!-- 附件 -->
          <div v-if="selectedAnnouncement.attachments && selectedAnnouncement.attachments.length > 0" class="mt-6 pt-6 border-t">
            <h4 class="text-lg font-semibold text-gray-900 mb-3">{{ $t('announcements.attachments') }}</h4>
            <div class="space-y-2">
              <a
                v-for="(attachment, index) in selectedAnnouncement.attachments"
                :key="index"
                :href="attachment.url"
                target="_blank"
                class="flex items-center gap-2 text-orange-600 hover:text-orange-700"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>{{ attachment.name }}</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const API_BASE_URL = process.env.VUE_APP_API_BASE_URL || '/api';

export default {
  name: 'AnnouncementList',
  data() {
    return {
      banner: {
        image_url: ''
      },
      categories: [
        { key: 'company' },
        { key: 'product' },
        { key: 'system' },
        { key: 'regulation' }
      ],
      selectedCategory: 'all',
      announcements: [],
      currentPage: 1,
      pageSize: 10,
      totalPages: 0,
      total: 0,
      selectedAnnouncement: null
    };
  },
  computed: {
    currentLang() {
      return this.$i18n.locale || 'zh';
    },
    filteredAnnouncements() {
      if (this.selectedCategory === 'all') {
        return this.announcements;
      }
      return this.announcements.filter(a => a.category === this.selectedCategory);
    },
    displayedPages() {
      const delta = 2;
      const rangeWithDots = [];

      for (let i = Math.max(2, this.currentPage - delta); i <= Math.min(this.totalPages - 1, this.currentPage + delta); i++) {
        rangeWithDots.push(i);
      }

      if (this.currentPage - delta > 2) {
        rangeWithDots.unshift('...');
      }

      if (this.currentPage + delta < this.totalPages - 1) {
        rangeWithDots.push('...');
      }

      if (this.totalPages > 0) {
        rangeWithDots.unshift(1);
        if (this.totalPages > 1) {
          rangeWithDots.push(this.totalPages);
        }
      }

      return [...new Set(rangeWithDots)].sort((a, b) => {
        if (a === '...') return 1;
        if (b === '...') return -1;
        return a - b;
      });
    }
  },
  async mounted() {
    await this.loadAnnouncements();

    // 监听语言切换
    window.addEventListener('localeChange', this.handleLocaleChange);
  },
  beforeDestroy() {
    window.removeEventListener('localeChange', this.handleLocaleChange);
  },
  methods: {
    async loadAnnouncements() {
      try {
        const params = new URLSearchParams({
          page: this.currentPage,
          page_size: this.pageSize,
          lang: this.currentLang
        });

        if (this.selectedCategory !== 'all') {
          params.append('category', this.selectedCategory);
        }

        const response = await fetch(`${API_BASE_URL}/public/announcements?${params}`);
        const data = await response.json();

        if (data.code === 0) {
          const announcements = data.data.list || [];
          const totalPages = data.data.total_pages || 1;
          const total = data.data.total || 0;

          // 如果当前语言没有数据，且不是简体中文，则fallback到简体中文
          if (announcements.length === 0 && this.currentLang !== 'zh') {
            console.log(`${this.currentLang} language has no announcements data, falling back to zh`);
            const fallbackParams = new URLSearchParams({
              page: this.currentPage,
              page_size: this.pageSize,
              lang: 'zh'
            });

            if (this.selectedCategory !== 'all') {
              fallbackParams.append('category', this.selectedCategory);
            }

            const fallbackResponse = await fetch(`${API_BASE_URL}/public/announcements?${fallbackParams}`);
            const fallbackData = await fallbackResponse.json();

            if (fallbackData.code === 0) {
              this.announcements = fallbackData.data.list || [];
              this.totalPages = fallbackData.data.total_pages || 1;
              this.total = fallbackData.data.total || 0;
            } else {
              this.announcements = [];
              this.totalPages = 1;
              this.total = 0;
            }
          } else {
            this.announcements = announcements;
            this.totalPages = totalPages;
            this.total = total;
          }
        }
      } catch (error) {
        console.error('加载公告失败:', error);
      }
    },
    filterCategory(category) {
      this.selectedCategory = category;
      this.currentPage = 1;
      this.loadAnnouncements();
    },
    goToPage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
      this.loadAnnouncements();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    viewAnnouncement(announcement) {
      this.selectedAnnouncement = announcement;

      // 增加浏览次数
      this.incrementViews(announcement.id);
    },
    async incrementViews(id) {
      try {
        await fetch(`${API_BASE_URL}/public/announcements/${id}/view`, {
          method: 'POST'
        });
      } catch (error) {
        console.error('增加浏览次数失败:', error);
      }
    },
    closeDetail() {
      this.selectedAnnouncement = null;
    },
    getCategoryLabel(category) {
      const labelMap = {
        company: this.$t('announcements.categoryCompany'),
        product: this.$t('announcements.categoryProduct'),
        system: this.$t('announcements.categorySystem'),
        regulation: this.$t('announcements.categoryRegulation')
      };
      return labelMap[category] || category;
    },
    getCategoryClass(category) {
      const classes = {
        company: 'bg-blue-100 text-blue-700',
        product: 'bg-green-100 text-green-700',
        system: 'bg-yellow-100 text-yellow-700',
        regulation: 'bg-red-100 text-red-700'
      };
      return classes[category] || 'bg-gray-100 text-gray-700';
    },
    getDay(dateStr) {
      const date = new Date(dateStr);
      return date.getDate();
    },
    getMonthYear(dateStr) {
      const date = new Date(dateStr);
      const months = this.currentLang === 'en'
        ? ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        : ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'];
      return months[date.getMonth()];
    },
    formatDate(dateStr) {
      const date = new Date(dateStr);
      const locale = this.currentLang === 'en'
        ? 'en-US'
        : (this.currentLang === 'zh-Hant' ? 'zh-TW' : 'zh-CN');
      return date.toLocaleDateString(locale);
    },
    handleLocaleChange() {
      this.loadAnnouncements();
    }
  }
};
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* 富文本内容样式 */
.prose >>> h1 {
  font-size: 1.875rem;
  font-weight: 700;
  margin-top: 1.5em;
  margin-bottom: 0.75em;
  line-height: 1.2;
  color: #111827;
}

.prose >>> h2 {
  font-size: 1.5rem;
  font-weight: 600;
  margin-top: 1.25em;
  margin-bottom: 0.75em;
  line-height: 1.3;
  color: #111827;
}

.prose >>> h3 {
  font-size: 1.25rem;
  font-weight: 600;
  margin-top: 1em;
  margin-bottom: 0.5em;
  line-height: 1.4;
  color: #111827;
}

.prose >>> p {
  margin-top: 0.5em;
  margin-bottom: 1em;
  line-height: 1.75;
  color: #4b5563;
}

.prose >>> ul {
  list-style-type: disc;
  padding-left: 1.5em;
  margin-top: 0.75em;
  margin-bottom: 1em;
}

.prose >>> ol {
  list-style-type: decimal;
  padding-left: 1.5em;
  margin-top: 0.75em;
  margin-bottom: 1em;
}

.prose >>> li {
  margin-top: 0.5em;
  margin-bottom: 0.5em;
  line-height: 1.75;
  color: #4b5563;
}

.prose >>> li::marker {
  color: #f97316;
}

.prose >>> strong {
  font-weight: 700;
  color: #111827;
}

.prose >>> em {
  font-style: italic;
}

.prose >>> a {
  color: #ea580c;
  text-decoration: underline;
  transition: color 0.2s;
}

.prose >>> a:hover {
  color: #c2410c;
}

.prose >>> ul ul,
.prose >>> ol ul,
.prose >>> ul ol,
.prose >>> ol ol {
  margin-top: 0.5em;
  margin-bottom: 0.5em;
}
</style>
